/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from 'react';
import { getPaginatedBlogs, searchBlogs, likeBlog, dislikeBlog } from '../../utils/api';

import './BlogList.css';
import {
  Card,
  CardBody,
  CardFooter,
  Image,
  Badge,
  Text,
  Heading,
  Box,
  Flex,
  Button,
  Stack,
  Modal,
  ModalContent,
  ModalOverlay,
  useDisclosure,
} from '@chakra-ui/react';

import { BiLike } from 'react-icons/bi';
import { AiFillLike } from 'react-icons/ai';

import ShareButton from './ShareButton';
import BlogModalContent from './BlogModalContent';
import Pagination from './Pagination';
import SearchBar from './SearchBar';
import Loading from '../../shared/Loading/Loading'
import AdminCredential from '../../auth/AdminCredential';

const OverlayOne = () => (
  <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px) hue-rotate(90deg)" />
);

const BlogList = () => {
  const [blogs, setBlogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    handleSearch();
  }, []);

  const handleSearch = async () => {
    try {
      setLoading(true);
      const searchResult = await searchBlogs(searchQuery, page, 6);
      setBlogs(searchResult.blogs);
      setTotalCount(searchResult.totalCount);
      setSelectedBlog(null);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePagination = async (newPage) => {
    try {
      setLoading(true);
      const paginatedBlogs = await getPaginatedBlogs(newPage, 6);
     
      setBlogs(paginatedBlogs.blogs);
      setTotalCount(paginatedBlogs.totalCount);
      setPage(newPage);
      setSelectedBlog(null);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };


  const handleBlogClick = (blog) => {
    setSelectedBlog(blog);
    onOpen();
  };

  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleShare = async (blog) => {
    try {
      // Check if the browser supports the `navigator.share()` method
      if (navigator.share) {
        // Prepare the data to be shared (e.g., title and URL)
        const shareData = {
          title: blog.title,
          url: window.location.href, // You can customize this URL based on your actual blog URL
        };

        // Trigger the native share dialog
        await navigator.share(shareData);
      } else {
        // If the `navigator.share()` method is not supported, you can provide a fallback option
        alert('Sharing is not supported on this browser.');
      }
    } catch (error) {
      console.error('Failed to share:', error);
    }
  };



  const handleLike = async (blogId, liked) => {
    try {
      if (liked) {
        await dislikeBlog(blogId);
      } else {
        await likeBlog(blogId);
      }
  
      const updatedBlogs = [...blogs];
      const blogIndex = updatedBlogs.findIndex((blog) => blog._id === blogId);
      
      if (blogIndex !== -1) {
        updatedBlogs[blogIndex].liked = !liked;
        updatedBlogs[blogIndex].likeCount += liked ? -1 : 1;
        setBlogs(updatedBlogs);
        
        // Update likedBlogs in localStorage
        const likedBlogs = JSON.parse(localStorage.getItem('likedBlogs')) || [];
        if (liked) {
          const updatedLikedBlogs = likedBlogs.filter((likedBlogId) => likedBlogId !== blogId);
          localStorage.setItem('likedBlogs', JSON.stringify(updatedLikedBlogs));
        } else {
          const updatedLikedBlogs = [...likedBlogs, blogId];
          localStorage.setItem('likedBlogs', JSON.stringify(updatedLikedBlogs));
        }
      }
    } catch (error) {
      console.error('Failed to update like/dislike:', error);
    }
  };


  
  return (
    <div className="flex justify-center">
      <div className="container mx-auto px-14">


        <SearchBar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          handleSearch={handleSearch}
          loading={loading}
        />
        {loading ? (
         <Loading/>
        ) : (
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-16">
            {blogs.map((blog) => (
              <Card key={blog._id}>
                <CardBody p="0" overflow="hidden">
                  <Box className="cursor-pointer" onClick={() => handleBlogClick(blog)}>
                    <Flex spacing="4">
                      <Flex flex="1" gap="4" justify="space-between" p="3" alignItems="center" flexWrap="wrap">
                        <Stack direction="row">
                          <Badge colorScheme="green">{blog.badges}</Badge>
                        </Stack>
                        <Box>
                          <Text opacity=".8" as="i">
                            {blog.author}
                          </Text>
                        </Box>
                      </Flex>
                    </Flex>
                    <Image
                      objectFit="cover"
                      src={blog.image}
                      alt={blog.title}
                      className="w-full h-48"
                      transition="transform 0.2s"
                      _hover={{ transform: 'scale(1.05)' }}
                    />
                    <Box p="4">
                      <Heading size="md" fontWeight="bold" textAlign="left" pb="3">
                        {blog.title}
                      </Heading>
                      <p color="gray.700" className="text-slate-300">
                        {blog.desc?.length > 75 ? blog.desc.substring(0, 75) + '...' : blog.desc}
                      </p>
                    </Box>
                  </Box>
                </CardBody>
                <CardFooter justify="space-between" flexWrap="wrap">
                <Button
                  flex="1"
                  variant="ghost"
                  color="whiteAlpha"
                  leftIcon={blog.liked ? <AiFillLike /> : <BiLike />}
                  onClick={() => handleLike(blog._id, blog.liked)}
                >
                  {blog.likeCount > 0 ? `${blog.likeCount} Likes` : 'Like'}
                </Button>
                  <ShareButton key={blog.id} blog={blog} handleShare={handleShare} />
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
        <div className="flex justify-center mt-4">
          <Pagination
            page={page}
            totalPages={Math.ceil(totalCount / 6)}
            onPageChange={handlePagination}
          />
        </div>


      </div>
      {selectedBlog && (
        <Modal isCentered isOpen={isOpen} onClose={onClose} size="xl">
          <OverlayOne />
          <ModalContent>
            <BlogModalContent blog={selectedBlog} onClose={onClose} />
          </ModalContent>
        </Modal>
      )}

    </div>
  );
};

export default BlogList;
